import { CSSProperties, useEffect, useState } from "react";
import { createTheme, getTheme, loadTheme } from "@fluentui/react";
import { IBrandColors, defaultTheme, fallbackTheme, getColor, getIcon, themeUtils } from "cayo.ui";
import { IconInfo, JobState } from "@cayo/shared/services/jobState/types";
import { IAppTheme } from "@cayo/shared/themes/types";
import { ITheme as IServerTheme } from "../api/schema.api";
import ajax from "@cayo/shared/libs/ajax";
import JobStateService from "../services/job-state.service";

interface IElementStyles {
  [key: string]: Record<string, string>;
}

export const defaultAppTheme = () => ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getJobState: (state: JobState): IconInfo => ({ iconName: "", color: "" }),
  ...defaultTheme(fallbackTheme.cayoTheme.brandColors),
});

export const fallbackAppTheme = defaultAppTheme();

type brandColorNames = keyof IBrandColors;

const themeCache = new Map<string, IAppTheme>();
const themeInFlight = new Map<string, Promise<IAppTheme>>();

// Palettes are stored separately so loadTheme can be re-called when switching back to a cached theme
const themePalettes = new Map<string, any>();

const buildTheme = (serverTheme: IServerTheme): IAppTheme => {
  const elementStyles = serverTheme.elementStyles as unknown as IElementStyles;
  const result = defaultTheme(serverTheme.brandColors, elementStyles) as IAppTheme;

  const resolveColor = (color: string) =>
    color.startsWith("#") ? color : (serverTheme.brandColors[color as brandColorNames] ?? color);

  result.getColor = (v, d) => getColor(v, d, serverTheme, resolveColor);
  result.getIcon = (v, d) => getIcon(serverTheme, v, d);
  result.resolveNamedColor = (c) => resolveColor(c);

  const jobStateService = new JobStateService(result);
  result.getJobState = (s) => jobStateService.getJobIcon(s);

  // TODO: remove hardcode
  result.getButtonThemeStyles = (b) => {
    const style = themeUtils.getButtonStyle(b);
    let hover: Partial<CSSProperties> | undefined = undefined;
    let pressed: Partial<CSSProperties> | undefined = undefined;

    // TODO: move 'promo' to server themes
    if (b.theme === "promo") {
      style.background = "linear-gradient(to right, #33d2ff, #7a5fff)";
      style.color = "white";
      style.border = "none";
      style.minWidth = 130;
    } else if (b.theme) {
      style.backgroundColor = result.getColor(b.theme, "severity") ?? b.theme;
      style.color =
        b.theme === "medium"
          ? serverTheme.brandColors.deleted
          : serverTheme.brandColors.primaryBackground;
      style.border = 0;
      style.transition = "background .5s";

      hover = {
        ...style,
        backgroundColor: themeUtils.lightenDarkenColor(style.backgroundColor, 20),
      };

      pressed = { ...style };
    }
    return { normal: style, hover, pressed };
  };

  // Use createTheme (local only) — loadTheme (global apply) is called separately
  // only when this theme becomes active, to avoid background fetches overwriting the
  // active Fluent UI global theme.
  const palette = serverTheme.officePatelle;
  result.officeTheme = palette ? createTheme({ palette }) : getTheme();

  return result;
};

const applyOfficeTheme = (name: string) => {
  const key = name.toLowerCase();
  const palette = themePalettes.get(key);
  if (palette) {
    loadTheme({ palette });
  }
};

export const getCachedServerTheme = (name: string) => {
  return themeCache.get(name.toLowerCase());
};

const fetchTheme = (name: string): Promise<IAppTheme> => {
  const key = name.toLowerCase();

  if (themeCache.has(key)) {
    return Promise.resolve(themeCache.get(key)!);
  }

  if (themeInFlight.has(key)) {
    return themeInFlight.get(key)!;
  }

  const promise = ajax
    .getClient(`themes/${name}`)
    .then((data: any) => {
      const serverTheme = data as IServerTheme;
      const built = buildTheme(serverTheme);
      themeCache.set(key, built);
      themePalettes.set(key, serverTheme.officePatelle);
      return built;
    })
    .finally(() => {
      themeInFlight.delete(key);
    });

  themeInFlight.set(key, promise);
  return promise;
};

export const useServerTheme = (themeName: string, knownThemes: string[]) => {
  const normalizedName = (themeName || "classic").toLowerCase();
  const [theme, setTheme] = useState<IAppTheme | undefined>(() => themeCache.get(normalizedName));
  const [isLoading, setIsLoading] = useState(!themeCache.has(normalizedName));

  useEffect(() => {
    let cancelled = false;

    if (knownThemes.length === 0) {
      setIsLoading(false);
      return;
    }

    if (themeCache.has(normalizedName)) {
      applyOfficeTheme(normalizedName);
      setTheme(themeCache.get(normalizedName));
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    // Warm the cache for all other known themes in the background
    knownThemes.forEach((name) => {
      if (name.toLowerCase() !== normalizedName) {
        fetchTheme(name);
      }
    });

    fetchTheme(normalizedName).then((built) => {
      if (!cancelled) {
        applyOfficeTheme(normalizedName);
        setTheme(built);
        setIsLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [normalizedName, knownThemes]);

  return { theme: theme || fallbackAppTheme, isLoading };
};
